import { memo } from 'react';
import { TMDB_IMAGE_URLS } from '../../constants';
import { useLanguage } from '../../contexts/language-context';
import { TeliaPlayLogo, Go3Logo, TV3PlayLogo, ForumCinemasLogo, CinamonLogo, getProviderDisplayName } from '../provider-logos';

const StreamingProviders = memo(({ 
  streamingProviders, 
  externalProviders,
  hasExternalAvailability,
  loading = false 
}) => {
  const { t } = useLanguage();
  
  const providerTypes = [
    { key: 'flatrate', labelKey: 'subscription', icon: '📺', color: 'from-blue-500 to-blue-600' },
    { key: 'buy', labelKey: 'buy', icon: '💳', color: 'from-green-500 to-green-600' },
    { key: 'rent', labelKey: 'rent', icon: '🎬', color: 'from-purple-500 to-purple-600' },
    { key: 'free', labelKey: 'free', icon: '🆓', color: 'from-orange-500 to-orange-600' }
  ];

  const hasAnyTMDBProviders = streamingProviders && typeof streamingProviders === 'object' && 
    providerTypes.some(type => streamingProviders[type.key]?.length > 0);

  // Show section if we have TMDB providers OR external providers
  if (!hasAnyTMDBProviders && !hasExternalAvailability) return null;

  if (loading) {
    return (
      <section>
        <div className="flex items-center space-x-3 mb-8">
          <div className="w-10 h-10 bg-dark-700 rounded-lg animate-pulse" />
          <div className="h-8 bg-dark-700 rounded-lg w-64 animate-pulse" />
        </div>
        
        <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl p-8 border border-dark-700/50">
          <div className="space-y-8">
            {[1, 2].map((section) => (
              <div key={section} className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-dark-600 rounded-lg animate-pulse" />
                  <div className="h-6 bg-dark-600 rounded-lg w-32 animate-pulse" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="bg-dark-700/50 rounded-xl p-4 space-y-3">
                      <div className="w-14 h-14 bg-dark-600 rounded-xl animate-pulse mx-auto" />
                      <div className="h-4 bg-dark-600 rounded-lg animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15M6 6v12a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2H8a2 2 0 00-2 2z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-white">{t('whereToWatch')}</h2>
      </div>
      
      <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-2xl p-8 border border-dark-700/50 shadow-2xl">
        <div className="space-y-8">
          {/* Kino teatrai — Forum Cinemas + Cinamon */}
          {hasExternalAvailability && (() => {
            const cinemaCards = [];
            
            // Only add Forum Cinemas if it exists and has no errors
            if (externalProviders?.forumCinemas?.exists && !externalProviders.forumCinemas.error) {
              cinemaCards.push({
                id: 'forumCinemas',
                name: 'Forum Cinemas',
                url: 'https://forumcinemas.lt',
                Logo: ForumCinemasLogo,
                hoverColor: 'hover:border-red-500/30',
                hoverText: 'group-hover:text-red-300',
                overlayColor: 'from-red-500/5 to-red-600/5',
                badgeColor: 'bg-red-500/15 text-red-400',
                cinemas: externalProviders.forumCinemas.programDetails?.cinemas || []
              });
            }
            
            // Only add Cinamon if it exists and has no errors
            if (externalProviders?.cinamon?.exists && !externalProviders.cinamon.error) {
              cinemaCards.push({
                id: 'cinamon',
                name: 'Cinamon',
                url: externalProviders.cinamon.programDetails?.url || 'https://cinamonkino.com/mega/lt',
                Logo: CinamonLogo,
                hoverColor: 'hover:border-orange-500/30',
                hoverText: 'group-hover:text-orange-300',
                overlayColor: 'from-orange-500/5 to-orange-600/5',
                badgeColor: 'bg-orange-500/15 text-orange-400',
                cinemas: externalProviders.cinamon.programDetails?.cinemas || []
              });
            }
            
            // Don't render the cinema section if no valid cinema providers
            if (cinemaCards.length === 0) return null;
            
            return (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-700 rounded-lg flex items-center justify-center">
                    <span className="text-white text-lg">🎬</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">Kino teatrai</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-dark-600 to-transparent"></div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {cinemaCards.map(card => (
                    <a
                      key={card.id}
                      href={card.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative bg-dark-700/50 backdrop-blur-sm rounded-xl p-4 hover:bg-dark-600/60 hover:scale-105 transition-all duration-300 cursor-pointer border border-dark-600/30 ${card.hoverColor}`}
                      title={card.name}
                    >
                      <div className="flex flex-col items-center space-y-3">
                        <div className="relative w-14 h-14 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow flex items-center justify-center bg-transparent">
                          <card.Logo />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                        <span className={`text-white text-sm font-medium text-center leading-tight ${card.hoverText} transition-colors`}>
                          {card.name}
                        </span>
                        {card.cinemas.length > 0 && (
                          <div className="flex flex-wrap justify-center gap-1">
                            {card.cinemas.map(c => (
                              <span key={c.key} className={`text-[10px] px-1.5 py-0.5 rounded-md font-medium leading-none ${card.badgeColor}`}>
                                {c.city}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${card.overlayColor} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                    </a>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* TMDB Providers + External Providers */}
          {providerTypes.map((type) => {
            const tmdbProviders = streamingProviders?.[type.key] || [];
            
            // Add external providers to appropriate categories based on pricing/subscription
            let allProviders = [...tmdbProviders];
            if (hasExternalAvailability && externalProviders) {
              const availableExternalProviders = Object.entries(externalProviders)
                .filter(([providerId, provider]) => 
                  provider?.exists && 
                  !provider?.error && 
                  providerId !== 'forumCinemas' && 
                  providerId !== 'cinamon'
                )
                .map(([providerId, provider]) => {
                  let price = 0;
                  let isFree = false;
                  let isSubscription = false;
                  let isRental = false;
                  let programDetails = provider.programDetails;
                  
                  // Handle Go3 - can be rental, subscription, or free depending on content
                  if (providerId === 'go3' && programDetails) {
                    price = programDetails.price || 0;
                    isFree = programDetails.isFree || false;
                    isSubscription = programDetails.isSubscription || false;
                    isRental = programDetails.isRental || false;
                    
                    // If it's VOD rental without a price, still show as rental
                    if (programDetails.isVod && !isFree && !isSubscription) {
                      isRental = true;
                    }
                  } else if (providerId === 'tv3Play' && programDetails) {
                    isFree = true;
                    isSubscription = false;
                    isRental = false;
                  }
                  // Handle Telia Play format: raw.programs[0].asset.p
                  else if (provider.raw?.programs?.length > 0) {
                    const program = provider.raw.programs[0];
                    if (program.asset?.p !== undefined) {
                      price = program.asset.p;
                      isFree = price === 0;
                      isRental = price > 0; // Telia Play shows rental prices
                      programDetails = {
                        price: price,
                        currency: 'EUR',
                        isFree: isFree,
                        isRental: isRental,
                        title: program.asset.label || program.asset.fullTitle,
                        originalTitle: program.asset.originalTitle
                      };
                    }
                  } 
                  // Fallback to programDetails if it exists
                  else if (programDetails?.price !== undefined) {
                    price = programDetails.price;
                    isFree = programDetails.isFree || price === 0;
                    isSubscription = programDetails.isSubscription || false;
                    isRental = programDetails.isRental || (price > 0 && !isSubscription);
                  }
                  
                  return {
                    provider_id: providerId,
                    provider_name: getProviderDisplayName(providerId),
                    logo_path: null,
                    isExternal: true,
                    externalId: providerId,
                    price: price,
                    currency: programDetails?.currency || 'EUR',
                    isFree: isFree,
                    isSubscription: isSubscription,
                    isRental: isRental,
                    programDetails: programDetails
                  };
                });
              
              // Add to appropriate category based on type
              if (type.key === 'flatrate') {
                // Add subscription-based providers to subscription category
                const subscriptionProviders = availableExternalProviders.filter(p => p.isSubscription && !p.isRental);
                allProviders = [...allProviders, ...subscriptionProviders];
              } else if (type.key === 'free') {
                // Add free external providers to free category
                const freeProviders = availableExternalProviders.filter(p => p.isFree && !p.isSubscription && !p.isRental);
                allProviders = [...allProviders, ...freeProviders];
              } else if (type.key === 'rent') {
                // Add rental providers to rent category (VOD rentals like Go3)
                const rentalProviders = availableExternalProviders.filter(p => p.isRental);
                allProviders = [...allProviders, ...rentalProviders];
              } else if (type.key === 'buy') {
                // Add paid external providers (purchases) to buy category
                const purchaseProviders = availableExternalProviders.filter(p => !p.isFree && !p.isSubscription && !p.isRental);
                allProviders = [...allProviders, ...purchaseProviders];
              }
              
              // Deduplicate providers by name (case-insensitive)
              // TMDB may already include Go3, Telia Play, etc., so remove external duplicates
              const seenNames = new Set();
              allProviders = allProviders.filter(provider => {
                const normalizedName = provider.provider_name?.toLowerCase() || '';
                if (seenNames.has(normalizedName)) {
                  return false; // Skip duplicate
                }
                seenNames.add(normalizedName);
                return true;
              });
            }

            if (allProviders.length === 0) return null;

            return (
              <div key={type.key} className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 bg-gradient-to-br ${type.color} rounded-lg flex items-center justify-center`}>
                    <span className="text-white text-lg">{type.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{t(type.labelKey)}</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-dark-600 to-transparent"></div>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {allProviders.map((provider) => {
                    const isTv3Link =
                      provider.isExternal &&
                      provider.externalId === 'tv3Play' &&
                      provider.programDetails?.url;
                    const cardClass =
                      'group relative bg-dark-700/50 backdrop-blur-sm rounded-xl p-4 hover:bg-dark-600/60 hover:scale-105 transition-all duration-300 cursor-pointer border border-dark-600/30 hover:border-primary-500/30';
                    const cardInner = (
                      <>
                        <div className="flex flex-col items-center space-y-3">
                          <div
                            className={`relative w-14 h-14 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-shadow flex items-center justify-center ${
                              provider.isExternal && provider.externalId === 'teliaPlay'
                                ? 'bg-black p-2'
                                : provider.isExternal && provider.externalId === 'go3'
                                  ? 'bg-transparent'
                                  : provider.isExternal && provider.externalId === 'tv3Play'
                                    ? 'bg-[#1a1a1a] p-1'
                                    : 'bg-white'
                            }`}
                          >
                            {provider.isExternal && provider.externalId === 'teliaPlay' ? (
                              <TeliaPlayLogo />
                            ) : provider.isExternal && provider.externalId === 'go3' ? (
                              <Go3Logo />
                            ) : provider.isExternal && provider.externalId === 'tv3Play' ? (
                              <TV3PlayLogo />
                            ) : (
                              <img
                                src={`${TMDB_IMAGE_URLS.ORIGINAL}${provider.logo_path}`}
                                alt={provider.provider_name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-white text-sm font-medium text-center leading-tight group-hover:text-primary-300 transition-colors">
                            {provider.provider_name}
                          </span>
                          {provider.isExternal && provider.isSubscription && !provider.isRental && (
                            <span className="text-blue-400 text-xs font-bold text-center">
                              {t('subscription')}
                            </span>
                          )}
                          {provider.isExternal && provider.isRental && (
                            <span className="text-purple-400 text-xs font-bold text-center">
                              {provider.price > 0
                                ? `${provider.price.toFixed(2)} ${provider.currency}`
                                : t('rent')}
                            </span>
                          )}
                          {provider.isExternal &&
                            provider.price !== undefined &&
                            provider.price > 0 &&
                            !provider.isFree &&
                            !provider.isSubscription &&
                            !provider.isRental && (
                              <span className="text-primary-400 text-xs font-bold text-center">
                                {provider.price.toFixed(2)} {provider.currency}
                              </span>
                            )}
                          {provider.isExternal && provider.isFree && !provider.isSubscription && !provider.isRental && (
                            <span className="text-green-400 text-xs font-bold text-center">
                              {t('free')}
                            </span>
                          )}
                        </div>
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-primary-500/5 to-primary-600/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      </>
                    );
                    return isTv3Link ? (
                      <a
                        key={provider.externalId}
                        href={provider.programDetails.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cardClass}
                        title={provider.provider_name}
                      >
                        {cardInner}
                      </a>
                    ) : (
                      <div
                        key={provider.isExternal ? provider.externalId : provider.provider_id}
                        className={cardClass}
                        title={provider.provider_name}
                      >
                        {cardInner}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

        </div>
      </div>
    </section>
  );
});

StreamingProviders.displayName = 'StreamingProviders';

export default StreamingProviders; 